import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { imageUrl } from '../lib/images'
import ReviewsSlider from '../components/ReviewsSlider'
import SeoHead from '../components/SeoHead'
import WaveDivider from '../components/WaveDivider'
import { localizeTree } from '../i18n/localizeTree'

export default function HomePage({ lang = 'da' }) {
  const [parallaxOffset, setParallaxOffset] = useState(0)
  const bannerRef = useRef(null)

  useEffect(() => {
    let animationFrameId
    const handleScroll = () => {
      if (bannerRef.current) {
        const rect = bannerRef.current.getBoundingClientRect()
        const windowHeight = window.innerHeight
        if (rect.top < windowHeight && rect.bottom > 0) {
          const progress = (windowHeight - rect.top) / (windowHeight + rect.height)
          setParallaxOffset((progress - 0.5) * 80)
        }
      }
    }

    const onScroll = () => {
      cancelAnimationFrame(animationFrameId)
      animationFrameId = requestAnimationFrame(handleScroll)
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    handleScroll()
    return () => {
      window.removeEventListener('scroll', onScroll)
      cancelAnimationFrame(animationFrameId)
    }
  }, [])
  return localizeTree((
    <>
      <SeoHead pageId="home" lang="da" />

      {/* Hero Section with Pattern & Overlapping Circular Imagery */}
      <section className="relative bg-[#F4E3E5] pt-12 pb-24 md:pt-16 md:pb-32 overflow-hidden border-b border-[#C68D94]/20">
        {/* Subtle decorative cupcake curved background pattern */}
        <div className="absolute inset-0 opacity-20 pointer-events-none overflow-hidden">
          <svg
            className="w-full h-full"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <defs>
              <pattern
                id="cupcake-curved-pattern"
                x="0"
                y="0"
                width="100"
                height="70"
                patternUnits="userSpaceOnUse"
              >
                {/* Row 1: Broad cupcake frosting scalloped waves */}
                <path
                  d="M 0,18 C 12,3 38,3 50,18 C 62,3 88,3 100,18"
                  fill="none"
                  stroke="#C68D94"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                {/* Row 1 inner concentric frosting curve */}
                <path
                  d="M 10,18 C 18,7 32,7 40,18 M 60,18 C 68,7 82,7 90,18"
                  fill="none"
                  stroke="#C68D94"
                  strokeWidth="1"
                  strokeDasharray="2 3"
                  strokeLinecap="round"
                  opacity="0.65"
                />
                <circle cx="25" cy="5" r="1.5" fill="#C68D94" opacity="0.6" />
                <circle cx="75" cy="5" r="1.5" fill="#C68D94" opacity="0.6" />

                {/* Row 2 (offset): Alternating cupcake wave arches */}
                <path
                  d="M -25,53 C -13,38 13,38 25,53 C 37,38 63,38 75,53 C 87,38 113,38 125,53"
                  fill="none"
                  stroke="#C68D94"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                {/* Row 2 inner concentric frosting curve */}
                <path
                  d="M -15,53 C -7,42 7,42 15,53 M 35,53 C 43,42 57,42 65,53 M 85,53 C 93,42 107,42 115,53"
                  fill="none"
                  stroke="#C68D94"
                  strokeWidth="1"
                  strokeDasharray="2 3"
                  strokeLinecap="round"
                  opacity="0.65"
                />
                <circle cx="0" cy="40" r="1.5" fill="#C68D94" opacity="0.6" />
                <circle cx="50" cy="40" r="1.5" fill="#C68D94" opacity="0.6" />
                <circle cx="100" cy="40" r="1.5" fill="#C68D94" opacity="0.6" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#cupcake-curved-pattern)" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center justify-items-center lg:justify-items-stretch">
            {/* Left Column: Vintage Decorative Frame Callout */}
            <div className="order-2 lg:order-1 text-center lg:text-left flex flex-col items-center lg:items-start">
              <div className="aspect-square w-full max-w-[20rem] sm:max-w-[24rem] md:max-w-[26rem] border-2 border-white/80 bg-white/40 backdrop-blur-xs rounded-full p-6 sm:p-8 shadow-sm flex flex-col items-center justify-center text-center shrink-0">
                <span className="font-script text-2xl sm:text-3xl text-[#8E4C56] block mb-1">
                  Velkommen til
                </span>
                <h1 className="font-script text-4xl sm:text-5xl lg:text-6xl text-[#3E3435] font-normal leading-tight my-2">
                  Fanø Chokolade
                </h1>
                <div className="mt-2 sm:mt-3 inline-block">
                  <span className="text-[10px] sm:text-[11px] font-bold tracking-[0.25em] uppercase text-[#C68D94]">
                    PASSION PÅ FANØ
                  </span>
                </div>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row items-center gap-4">
                <Link
                  to="/aabningstider-og-adresse"
                  className="bg-[#C68D94] hover:bg-[#A35D67] text-white font-semibold text-xs tracking-wider uppercase px-8 py-3.5 rounded-full transition-all shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-[#A35D67]"
                >
                  KOM FORBI MIN CAFÉ
                </Link>
                <Link
                  to="/webshop"
                  className="bg-white/80 hover:bg-white text-[#3E3435] border border-[#C68D94]/40 font-semibold text-xs tracking-wider uppercase px-7 py-3.5 rounded-full transition-all"
                >
                  Se udvalg i shoppen
                </Link>
              </div>
            </div>

            {/* Right Column: Overlapping Circular Photography */}
            <div className="order-1 lg:order-2 relative flex justify-center items-center py-6 lg:py-8">
              {/* Primary Large Circle */}
              <div className="aspect-square w-[18rem] sm:w-[24rem] md:w-[28rem] lg:w-[32rem] xl:w-[36rem] rounded-full overflow-hidden border-4 border-white shadow-xl bg-stone-100 relative z-10 shrink-0">
                <img
                  src={imageUrl('/_assets/cake-05.jpg', 'width=1400,height=1400,fit=cover,quality=85,format=auto')}
                  alt="Håndlavet kage hos Fanø Chokolade"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Secondary Overlapping Small Circle */}
              <div className="absolute -bottom-6 -left-4 sm:-bottom-2 sm:-left-2 lg:bottom-4 lg:-left-4 aspect-square w-36 sm:w-48 md:w-56 lg:w-64 xl:w-72 rounded-full overflow-hidden border-4 border-white shadow-lg bg-stone-100 z-20 shrink-0">
                <img
                  src={imageUrl(
                    '/_assets/fanoechokolade-095-e1757667399155.jpg',
                    'width=800,height=800,fit=cover,quality=85,format=auto'
                  )}
                  alt="Chokolader og lækkerier"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Downward convex wave separator to next section */}
        <div className="absolute bottom-0 left-0 right-0 w-full overflow-hidden leading-none pointer-events-none transform translate-y-1">
          <svg
            viewBox="0 0 1440 60"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full h-8 sm:h-12 md:h-16 text-[#FDF8F7]"
            preserveAspectRatio="none"
          >
            <path
              d="M0,0 C360,50 1080,50 1440,0 L1440,60 L0,60 Z"
              fill="currentColor"
            />
          </svg>
        </div>
      </section>

      {/* Brand Story & Visual Collage ("Passion & Smag") */}
      <section className="py-16 sm:py-24 bg-[#FDF8F7] relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Top/Middle Arched Card with Exact Text */}
          <div className="max-w-md mx-auto rounded-t-full rounded-b-3xl bg-[#A35D67] text-white pt-24 sm:pt-28 pb-10 px-8 sm:px-10 text-center shadow-lg relative z-30 -mt-28 sm:-mt-36 md:-mt-40 lg:-mt-44 mb-14 border border-white/20">
            <h2 className="font-script text-4xl sm:text-5xl font-normal mb-5 text-white">
              Passion & smag
            </h2>
            <WaveDivider className="text-white/60 mx-auto mb-6" />
            <p className="text-xs sm:text-sm leading-relaxed font-light text-white/95">
              Fanø Chokolade Café er mit hjertebarn – et møde mellem håndværk, passion og kreativitet. Det, der startede som en hobby, er i dag min fuldtidsdrøm, hvor jeg skaber fyldte chokolader og byder gæster velkommen i min café ved Fanø Bad – altid med fokus på kvalitet og personlighed.
            </p>
            <div className="mt-7">
              <Link
                to="/om-mig-2"
                className="inline-block border border-white/80 hover:bg-white hover:text-[#A35D67] text-white text-xs uppercase font-semibold tracking-wider px-6 py-2 rounded-full transition-colors"
              >
                Læs hele min historie
              </Link>
            </div>
          </div>

          {/* Bottom Visual Collage Row from scrape */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
            {/* Left Photo: Packaged Chocolate */}
            <div className="rounded-t-full rounded-b-3xl overflow-hidden aspect-[4/3] shadow-sm border border-[#C68D94]/20 group">
              <img
                src={imageUrl(
                  '/_assets/whatsapp-image-2025-09-28-at-15-29-20-2a2b6f5e-1-e1759234908157.jpg',
                  'width=600,height=450,fit=cover,quality=80,format=auto'
                )}
                alt="Fanø Chokolade poser"
                className="w-full h-full object-cover transition-transform duration-500 "
              />
            </div>

            {/* Center Photo: Café Seating */}
            <div className="rounded-t-full rounded-b-3xl overflow-hidden aspect-[4/3] shadow-sm border border-[#C68D94]/20 group">
              <img
                src={imageUrl(
                  '/_assets/fanoechokolade-106-scaled-e1757667485907.jpg',
                  'width=600,height=450,fit=cover,quality=80,format=auto'
                )}
                alt="Café interiør ved Fanø Bad"
                className="w-full h-full object-cover transition-transform duration-500 "
              />
            </div>

            {/* Right Photo: Circular/Rounded Cupcakes & Delicacies */}
            <div className="rounded-t-full rounded-b-3xl overflow-hidden aspect-[4/3] shadow-sm border border-[#C68D94]/20 group">
              <img
                src={imageUrl(
                  '/_assets/311689161-204192008618363-7292969307395431332-n.jpg',
                  'width=600,height=450,fit=cover,quality=80,format=auto'
                )}
                alt="Håndlavede kager og chokolader"
                className="w-full h-full object-cover transition-transform duration-500 "
              />
            </div>
          </div>
        </div>
      </section>

      {/* Summary Highlight Section ("Kortsagt:") with Verbatim Text */}
      <section className="py-16 sm:py-20 bg-white border-y border-[#C68D94]/15 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left: Decorative Outline Rings */}
            <div className="md:col-span-7 relative flex justify-center items-center py-8">
              <div className="w-64 h-64 sm:w-80 sm:h-80 md:w-96 md:h-96 lg:w-[28rem] lg:h-[28rem] rounded-full border-2 border-[#C68D94]/30 flex items-center justify-center">
                <div className="w-48 h-48 sm:w-60 sm:h-60 md:w-72 md:h-72 lg:w-84 lg:h-84 rounded-full border-2 border-[#C68D94]/40 flex items-center justify-center bg-[#FDF8F7]">
                  <div className="w-32 h-32 sm:w-44 sm:h-44 md:w-52 md:h-52 lg:w-60 lg:h-60 rounded-full overflow-hidden shadow-inner">
                    <img
                      src={imageUrl(
                        '/_assets/img-20240629-wa0209-e1755501392617.jpg',
                        'width=600,height=600,fit=cover,quality=80,format=auto'
                      )}
                      alt="Fanø Chokolade detalje"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
              <div className="absolute -top-3 -right-3 w-32 h-32 rounded-full border border-[#C68D94]/25 pointer-events-none" />
              <div className="absolute -bottom-5 -left-3 w-36 h-36 rounded-full border border-[#C68D94]/20 pointer-events-none" />
            </div>

            {/* Right: Exact Scraped Kortsagt points */}
            <div className="md:col-span-5 md:justify-self-end w-full max-w-md">
              <h2 className="font-script text-4xl sm:text-5xl text-[#C68D94] font-normal my-2">
                Kortsagt:
              </h2>
              <WaveDivider className="text-[#C68D94]/60 mb-6" />

              <div className="space-y-4 text-xs sm:text-sm text-[#3E3435] font-light leading-relaxed">
                <div className="flex items-start gap-3">
                  <div className="text-[#A35D67] flex items-center justify-center shrink-0 mt-0.5 w-5">
                    <i className="fa-solid fa-store text-sm" aria-hidden="true" />
                  </div>
                  <h3 className="font-medium text-[#3E3435]">
                    Fantastisk lille café på Fanø - Strandvejen 58, 6720 Fanø
                  </h3>
                </div>

                <div className="flex items-start gap-3">
                  <div className="text-[#A35D67] flex items-center justify-center shrink-0 mt-0.5 w-5">
                    <i className="fa-solid fa-cookie-bite text-sm" aria-hidden="true" />
                  </div>
                  <h3 className="font-medium text-[#3E3435]">
                    Friske chokolader, kager og lækre fristelser
                  </h3>
                </div>

                <div className="flex items-start gap-3">
                  <div className="text-[#A35D67] flex items-center justify-center shrink-0 mt-0.5 w-5">
                    <i className="fa-solid fa-cake-candles text-sm" aria-hidden="true" />
                  </div>
                  <h3 className="font-medium text-[#3E3435]">
                    Kager efter bestilling til alle begivenheder
                  </h3>
                </div>

                <div className="flex items-start gap-3">
                  <div className="text-[#A35D67] flex items-center justify-center shrink-0 mt-0.5 w-5">
                    <i className="fa-solid fa-mug-saucer text-sm" aria-hidden="true" />
                  </div>
                  <h3 className="font-medium text-[#3E3435]">
                    Hyggestemning på Fanø
                  </h3>
                </div>
              </div>

              <div className="mt-8 flex flex-col lg:flex-row items-center gap-4">
                <Link
                  to="/aabningstider-og-adresse"
                  className="bg-[#C68D94] hover:bg-[#A35D67] text-white text-xs font-semibold uppercase tracking-wider px-6 py-3 rounded-full transition-colors shadow-sm"
                >
                  Find Vej Til Caféen
                </Link>
                <Link
                  to="/forspoergsel"
                  className="text-xs font-semibold uppercase tracking-wider text-[#A35D67] hover:underline"
                >
                  Bestil kage til fest →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Infinite Reviews Slider ("Glade Kunder") */}
      <ReviewsSlider />

      {/* Full-Width Visual Bakery Banner from the scraped images */}
      <section ref={bannerRef} className="relative w-full h-72 sm:h-96 overflow-hidden">
        <img
          src={imageUrl(
            '/_assets/235247020-113201801050718-2847010287097278990-n.jpg',
            'width=1920,height=800,fit=cover,quality=85,format=auto'
          )}
          alt="Friskbagte kager og chokoladeforme i bageriet"
          className="absolute inset-x-0 -top-[15%] w-full h-[130%] object-cover will-change-transform pointer-events-none"
          style={{
            transform: `translate3d(0, ${parallaxOffset}px, 0)`,
          }}
        />
        <div className="absolute inset-0 bg-stone-900/35 flex items-center justify-center p-6 text-center">
          <div className="max-w-xl text-white">
            <h2 className="font-script text-4xl sm:text-5xl mb-3 font-normal drop-shadow">
              Håndlavet med kærlighed
            </h2>
            <p className="text-xs sm:text-sm font-light leading-relaxed drop-shadow mb-6">
              Hver eneste chokolade og kage formes, pyntes og smages til med stolthed på Fanø.
            </p>
            <Link
              to="/webshop"
              className="inline-block bg-white text-[#3E3435] hover:bg-[#F7ECEE] hover:text-[#A35D67] px-7 py-3 rounded-full text-xs font-semibold tracking-wider uppercase transition-colors shadow-md"
            >
              Gå på opdagelse i Webshoppen
            </Link>
          </div>
        </div>
      </section>
    </>
  ), lang)
}
